import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch, apiBaseUrl } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface PeriodCloseResponse {
  close_id: string;
  period_type: string;
  period: string;
  status: string;
  closed_at: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function buildForm23Url(baseUrl: string, period: string): string {
  return `${baseUrl}/finance/tax/form-23/${period}/download`;
}

function downloadUrl(path: string): string {
  return `${apiBaseUrl()}${path}`;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceTaxForm23Page() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data: closesData, isPending, isError } = useQuery({
    queryKey: ['finance', 'period-closes'],
    queryFn: () => apiFetch<PeriodCloseResponse[]>('/finance/period-closes'),
    refetchInterval: 30_000,
  });

  const downloadLinkStyle: React.CSSProperties = {
    display: 'inline-block',
    background: 'rgba(125,211,252,0.10)',
    border: '1px solid rgba(125,211,252,0.35)',
    borderRadius: 4, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 700, padding: '6px 16px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };

  const quickBtnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 10px', cursor: 'pointer', textDecoration: 'none',
    display: 'inline-block',
  };

  const closedPeriods = closesData?.filter(c => c.status === 'closed' || c.status === 'approved') ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Taxes', to: '/omega/finance/tax' },
        { label: 'Form 23' },
      ]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Form 23
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Download filing packages for the Tax Authority (form 23 — Form 23)
        </div>
      </div>

      {/* Period selector + download */}
      <div style={{
        background: 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6, padding: '16px 20px', marginBottom: 24, maxWidth: 480,
      }}>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Select period
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={{ ...inputStyle, width: 100 }}
          />
          <a
            href={downloadUrl(`/finance/tax/form-23/${period}/download`)}
            download
            style={downloadLinkStyle}
          >
            Download form XML
          </a>
        </div>
        <div style={{ marginTop: 8, fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
          {buildForm23Url(apiBaseUrl(), period)}
        </div>
      </div>

      {/* Quick-select from closed periods */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Closed periods
        </div>
        {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}
        {closedPeriods.length === 0 && !isPending && !isError && (
          <div style={{ color: 'var(--omega-ink-muted)', fontSize: '9pt', fontFamily: 'var(--omega-mono)' }}>
            No closed periods.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {closedPeriods.map(c => (
            <a
              key={c.close_id}
              href={downloadUrl(`/finance/tax/form-23/${c.period}/download`)}
              download
              style={quickBtnStyle}
              onClick={() => setPeriod(c.period)}
            >
              {c.period} ↓
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
